export interface RuntimeBuildIdentity {
  buildId: string;
  commitSha: string | null;
  buildTimestamp: string | null;
  appEntry: string;
  mode: string;
}

export interface AbortDiagnosticClassification {
  isAbortLike: boolean;
  name: string | null;
  message: string | null;
}

type DiagnosticEvent = Readonly<{
  sequence: number;
  timestamp: string;
  event: string;
  buildId: string;
  pathname: string;
  componentInstanceId?: string;
  unitId?: string;
  entrypoint?: string;
  routeType?: string;
  branch?: string;
  source?: string;
  errorName?: string | null;
  errorMessage?: string | null;
  isAbortLike?: boolean;
  filename?: string | null;
  lineno?: number | null;
  colno?: number | null;
  componentStack?: string | null;
  scriptUrl?: string | null;
}>;

type LoadedScriptSummary = Readonly<{
  pathname: string;
  filename: string;
  type: string;
}>;

type ServiceWorkerSummary = Readonly<{
  supported: boolean;
  serviceWorkerRegistered: boolean;
  controllerPresent: boolean;
  controllerScriptURL: string | null;
  registrations: ReadonlyArray<{
    scope: string;
    activeScriptURL: string | null;
    waitingScriptURL: string | null;
    installingScriptURL: string | null;
    updateViaCache: string | null;
  }>;
  cacheNames: ReadonlyArray<string>;
}>;

type RuntimeDiagnosticsSnapshot = Readonly<{
  identity: RuntimeBuildIdentity;
  activeEntrypoint: string;
  events: ReadonlyArray<DiagnosticEvent>;
  loadedScripts: ReadonlyArray<LoadedScriptSummary>;
  serviceWorker: ServiceWorkerSummary;
  pathname: string;
  userAgentSummary: string | null;
}>;

declare global {
  interface Window {
    __MINTLEAF_RUNTIME_IDENTITY__?: RuntimeBuildIdentity;
    __MINTLEAF_GET_RUNTIME_DIAGNOSTICS__?: () => RuntimeDiagnosticsSnapshot;
    __MINTLEAF_RUNTIME_DIAGNOSTICS_REGISTERED__?: boolean;
  }
}

const env = (import.meta as any).env || {};
const BUILD_ID = String(env.VITE_MINTLEAF_BUILD_ID || env.VITE_COMMIT_SHA || env.VITE_GIT_COMMIT_SHA || env.MODE || 'development-hmr');
const COMMIT_SHA = typeof env.VITE_COMMIT_SHA === 'string' && env.VITE_COMMIT_SHA.trim()
  ? env.VITE_COMMIT_SHA.trim()
  : typeof env.VITE_GIT_COMMIT_SHA === 'string' && env.VITE_GIT_COMMIT_SHA.trim()
    ? env.VITE_GIT_COMMIT_SHA.trim()
    : null;
const BUILD_TIMESTAMP = typeof env.VITE_BUILD_TIMESTAMP === 'string' && env.VITE_BUILD_TIMESTAMP.trim()
  ? env.VITE_BUILD_TIMESTAMP.trim()
  : null;
const MODE = String(env.MODE || (env.DEV ? 'development' : 'production'));

let activeEntrypoint = 'unknown';
let sequence = 0;
let serviceWorkerSummary: ServiceWorkerSummary = {
  supported: false,
  serviceWorkerRegistered: false,
  controllerPresent: false,
  controllerScriptURL: null,
  registrations: [],
  cacheNames: [],
};
const RING_LIMIT = 250;
const events: DiagnosticEvent[] = [];
let instanceCounter = 0;

const safePathname = () => typeof window === 'undefined' ? '' : window.location?.pathname || '';
const isoNow = () => new Date().toISOString();

const sanitizeUrlPath = (raw: string | null | undefined): string | null => {
  if (!raw) return null;
  try {
    const url = new URL(raw, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    return url.pathname;
  } catch {
    return raw.split('?')[0].split('#')[0];
  }
};

export const getRuntimeBuildIdentity = (appEntry = activeEntrypoint): RuntimeBuildIdentity => ({
  buildId: BUILD_ID,
  commitSha: COMMIT_SHA,
  buildTimestamp: BUILD_TIMESTAMP,
  appEntry,
  mode: MODE,
});

export const classifyAbortLikeError = (error: unknown): AbortDiagnosticClassification => {
  const record = error && typeof error === 'object' ? error as { name?: unknown; message?: unknown } : null;
  const name = typeof record?.name === 'string' ? record.name : error instanceof Error ? error.name : null;
  const message = typeof record?.message === 'string' ? record.message : error instanceof Error ? error.message : typeof error === 'string' ? error : null;
  const normalizedMessage = (message || '').toLowerCase();
  return {
    name,
    message,
    isAbortLike: name === 'AbortError' || normalizedMessage === 'the user aborted a request.' || normalizedMessage === 'the operation was aborted.',
  };
};

export const sanitizeError = (error: unknown) => {
  const abort = classifyAbortLikeError(error);
  return {
    errorName: abort.name || (error instanceof Error ? error.name : typeof error),
    errorMessage: abort.message || null,
    isAbortLike: abort.isAbortLike,
  };
};

export const shouldLogRuntimeDiagnosticsToConsole = (): boolean => {
  if (env.DEV === true) return true;
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('mintleafDiagnostics') === '1';
};

export const recordRuntimeDiagnosticEvent = (input: Omit<DiagnosticEvent, 'sequence' | 'timestamp' | 'buildId' | 'pathname'> & { timestamp?: string }): DiagnosticEvent => {
  const event: DiagnosticEvent = Object.freeze({
    ...input,
    sequence: ++sequence,
    timestamp: input.timestamp || isoNow(),
    buildId: getRuntimeBuildIdentity().buildId,
    pathname: safePathname(),
  });
  events.push(event);
  if (events.length > RING_LIMIT) events.splice(0, events.length - RING_LIMIT);
  if (shouldLogRuntimeDiagnosticsToConsole()) console.info('[ReservationDiagnostics]', event);
  return event;
};

export const createReservationDiagnosticInstanceId = (): string => `reservation-page-${++instanceCounter}`;

export const listLoadedApplicationScripts = (): LoadedScriptSummary[] => {
  if (typeof document === 'undefined') return [];
  return Array.from(document.scripts)
    .map(script => {
      const pathname = sanitizeUrlPath(script.src) || '';
      const filename = pathname.split('/').filter(Boolean).pop() || pathname;
      return { pathname, filename, type: script.type || 'classic' };
    })
    .filter(script => script.pathname.includes('/assets/') || script.filename.includes('index'));
};

export const refreshServiceWorkerDiagnostics = async (): Promise<ServiceWorkerSummary> => {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    serviceWorkerSummary = { supported: false, serviceWorkerRegistered: false, controllerPresent: false, controllerScriptURL: null, registrations: [], cacheNames: [] };
    return serviceWorkerSummary;
  }
  const registrations = await navigator.serviceWorker.getRegistrations().catch(() => []);
  const cacheNames = typeof caches !== 'undefined' ? await caches.keys().catch(() => []) : [];
  serviceWorkerSummary = {
    supported: true,
    serviceWorkerRegistered: registrations.length > 0,
    controllerPresent: Boolean(navigator.serviceWorker.controller),
    controllerScriptURL: sanitizeUrlPath(navigator.serviceWorker.controller?.scriptURL) || null,
    registrations: registrations.map(registration => ({
      scope: sanitizeUrlPath(registration.scope) || '',
      activeScriptURL: sanitizeUrlPath(registration.active?.scriptURL) || null,
      waitingScriptURL: sanitizeUrlPath(registration.waiting?.scriptURL) || null,
      installingScriptURL: sanitizeUrlPath(registration.installing?.scriptURL) || null,
      updateViaCache: registration.updateViaCache || null,
    })),
    cacheNames,
  };
  return serviceWorkerSummary;
};

export const getRuntimeDiagnosticsSnapshot = (): RuntimeDiagnosticsSnapshot => ({
  identity: getRuntimeBuildIdentity(activeEntrypoint),
  activeEntrypoint,
  events: [...events],
  loadedScripts: listLoadedApplicationScripts(),
  serviceWorker: serviceWorkerSummary,
  pathname: safePathname(),
  userAgentSummary: typeof navigator === 'undefined' ? null : navigator.userAgent,
});

const exposeGlobals = () => {
  if (typeof window === 'undefined') return;
  Object.defineProperty(window, '__MINTLEAF_RUNTIME_IDENTITY__', {
    configurable: true,
    enumerable: false,
    get: () => Object.freeze({ ...getRuntimeBuildIdentity(activeEntrypoint) }),
  });
  Object.defineProperty(window, '__MINTLEAF_GET_RUNTIME_DIAGNOSTICS__', {
    configurable: true,
    enumerable: false,
    value: () => getRuntimeDiagnosticsSnapshot(),
    writable: false,
  });
};

export const markRuntimeEntrypoint = (entrypoint: string): void => {
  activeEntrypoint = entrypoint;
  exposeGlobals();
  const currentScript = typeof document !== 'undefined' ? sanitizeUrlPath(document.currentScript?.getAttribute('src')) : null;
  const identity = getRuntimeBuildIdentity(entrypoint);
  console.info('[RuntimeIdentity] entrypoint', { ...identity, currentScriptUrl: currentScript });
  recordRuntimeDiagnosticEvent({ event: 'runtime:entrypoint', entrypoint, scriptUrl: currentScript || undefined });
  void refreshServiceWorkerDiagnostics().then(() => recordRuntimeDiagnosticEvent({ event: 'runtime:service-worker:inspected', entrypoint }));
};

export const registerGlobalRuntimeErrorDiagnostics = (): void => {
  if (typeof window === 'undefined' || window.__MINTLEAF_RUNTIME_DIAGNOSTICS_REGISTERED__) return;
  window.__MINTLEAF_RUNTIME_DIAGNOSTICS_REGISTERED__ = true;
  window.addEventListener('error', event => {
    const safe = sanitizeError(event.error || event.message);
    recordRuntimeDiagnosticEvent({
      event: 'global:error',
      source: 'window.error',
      errorName: safe.errorName,
      errorMessage: safe.errorMessage || (typeof event.message === 'string' ? event.message : null),
      isAbortLike: safe.isAbortLike,
      filename: sanitizeUrlPath(event.filename),
      lineno: typeof event.lineno === 'number' ? event.lineno : null,
      colno: typeof event.colno === 'number' ? event.colno : null,
    });
  });
  window.addEventListener('unhandledrejection', event => {
    const safe = sanitizeError(event.reason);
    recordRuntimeDiagnosticEvent({
      event: 'global:unhandledrejection',
      source: 'window.unhandledrejection',
      errorName: safe.errorName,
      errorMessage: safe.errorMessage,
      isAbortLike: safe.isAbortLike,
    });
  });
};
